import { List, Record } from "immutable";

import type { TicketAuditLogModel } from "./auditLogs";

import { type TTicketDuration } from "../utils/durationsOptions";
import { AuditLogModel } from "./auditLogs";
import { TicketApprovalRequestModel } from "./TicketApprovalRequestModel";
import { TicketApprovalResponseModel } from "./TicketApprovalResponseModel";
import { TicketCommentModel } from "./TicketCommentModel";
import { TicketingIntegrationTicketModel } from "./TicketingIntegrationTicketModel";
import { TicketPermissionModel } from "./TicketPermissionModel";
import { TicketTargetModel } from "./TicketTargetModel";

export enum TicketStatus {
  approved = "approved",
  cancelled = "cancelled",
  failed = "failed",
  granted = "granted",
  permissionInProgress = "permissionInProgress",
  rejected = "rejected",
  revoked = "revoked",
  waitingForApproval = "waitingForApproval",
  waitingForIT = "waitingForIT",
}

export type TTicketStatus = keyof typeof TicketStatus;
export const TICKET_STATUSES: TTicketStatus[] = Object.keys(TicketStatus) as TTicketStatus[];

const defaults = {
  approvalRequests: null as List<TicketApprovalRequestModel> | null,
  auditLogs: null as List<TicketAuditLogModel> | null,
  canUserRenew: false,
  comments: null as List<TicketCommentModel> | null,
  createdAt: new Date(0),
  creatorId: "",
  duration: 0 as TTicketDuration,
  id: "",
  justification: "",
  number: 0,
  receiverId: "",
  status: "" as TTicketStatus,
  targets: null as List<TicketTargetModel> | null,
  ticketApprovalResponses: null as List<TicketApprovalResponseModel> | null,
  ticketingIntegrationTicket: null as null | TicketingIntegrationTicketModel,
  ticketPermissions: null as List<TicketPermissionModel> | null,
  updatedAt: new Date(0),
};

export class TicketModel extends Record<typeof defaults>(defaults) {
  static fromServerData(data: unknown): TicketModel {
    const {
      approvalRequests,
      auditLogs,
      canUserRenew,
      comments,
      createdAt,
      creatorId,
      duration,
      id,
      number,
      receiverId,
      status,
      targets,
      ticketApprovalResponses,
      ticketingIntegrationTicket,
      ticketPermissions,
      updatedAt,
    } = data as typeof defaults;

    const commentsList = comments ? List(comments.map(TicketCommentModel.fromServerData)) : null;

    return new TicketModel({
      approvalRequests: approvalRequests
        ? List(approvalRequests.map(TicketApprovalRequestModel.fromServerData)).sortBy(({ sortOrder }) => sortOrder)
        : null,
      auditLogs: auditLogs
        ? List(auditLogs.map((log) => AuditLogModel.fromServerData(log) as TicketAuditLogModel))
        : null,
      canUserRenew: Boolean(canUserRenew),
      comments: commentsList,
      createdAt: new Date(createdAt),
      creatorId,
      duration,
      id,
      justification: commentsList && commentsList.size ? commentsList?.get(0)?.content : "",
      number,
      receiverId,
      status,
      targets: targets ? List(targets.map(TicketTargetModel.fromServerData)) : null,
      ticketApprovalResponses: ticketApprovalResponses
        ? List(ticketApprovalResponses.map(TicketApprovalResponseModel.fromServerData))
        : null,
      ticketingIntegrationTicket: ticketingIntegrationTicket
        ? TicketingIntegrationTicketModel.fromServerData(ticketingIntegrationTicket)
        : null,
      ticketPermissions: ticketPermissions
        ? List(
            ticketPermissions.map(TicketPermissionModel.fromServerData).sort((a, b) => {
              if (a.type === "regular") return -1;
              if (b.type === "regular") return 1;
              return 0;
            }),
          )
        : null,
      updatedAt: new Date(updatedAt),
    });
  }
}
